import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import {
  CalendarDays,
  ChevronLeft,
  Home,
  ScrollText,
  FileSpreadsheet,
  Plus,
  UserPlus,
  Settings as SettingsIcon,
  Menu,
  MessageSquare,
  Shield
} from 'lucide-react';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useAuth } from '../../context/AuthContext';
import { employeeNamesService } from '../../services/employeeNames';
import { UserInfoSection } from '../layout/UserInfoSection';
import { FeedbackModal } from '../shared/FeedbackModal';
import '../../styles/dialog-select.css';
import '../../styles/day-away-favorites-override.css';

const DayAwayLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Day Away Board';
    
    if (path === '/day-away') {
      title = 'Dashboard | Day Away Board';
    } else if (path.startsWith('/day-away/calendar')) {
      title = 'Calendar | Day Away Board';
    } else if (path.startsWith('/day-away/ledger')) {
      title = 'Ledger | Day Away Board';
    } else if (path.startsWith('/day-away/dashboard-sheet')) {
      title = 'Dashboard Sheet | Day Away Board';
    } else if (path.startsWith('/day-away/my-team')) {
      title = 'My Team | Day Away Board';
    } else if (path.startsWith('/day-away/settings')) {
      title = 'Settings | Day Away Board';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const { isAdmin } = useIsAdmin();


  const navigation = [
    { name: 'Dashboard', href: '/day-away', icon: Plus },
    { name: 'My Team', href: '/day-away/my-team', icon: UserPlus },
    { name: 'Calendar', href: '/day-away/calendar', icon: CalendarDays },
    { name: 'Ledger', href: '/day-away/ledger', icon: ScrollText, hideOnMobile: true },
    { name: 'Dashboard Sheet', href: '/day-away/dashboard-sheet', icon: FileSpreadsheet, hideOnMobile: true },
  ];

  const handleFeedbackClick = () => {
    setIsFeedbackModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden day-away-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <MessageSquare className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          )}
        </button>
        <div className="flex-1 flex justify-center">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Day Away Board</h1>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
        transform transition-transform duration-300 ease-in-out flex flex-col
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
        ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
      `}>
        {/* Desktop Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="border-b dark:border-gray-700 flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/day-away')}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <CalendarDays className="h-12 w-12 text-cyan-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <CalendarDays className="h-20 w-20 text-cyan-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">DayAway</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Time Off Tracker</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {navigation.map((item) => {
            // Hide Ledger and Dashboard Sheet on mobile
            if (item.hideOnMobile && isMobileMenuOpen) {
              return null;
            }
            
            return (
              <div
                key={item.name}
                onClick={() => handleNavigate(item.href)}
                className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                  isActive(item.href) ? 'bg-cyan-50 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-300' :
                  'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <item.icon className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
                {(!isCollapsed || isMobileMenuOpen) && item.name}
              </div>
            );
          })}

          {/* Give Feedback - Part of App Unique Navigation */}
          <div
            onClick={handleFeedbackClick}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <MessageSquare className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Give Feedback'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* General Navigation */}
          
          {/* Settings */}
          <div
            onClick={() => handleNavigate('/day-away/settings')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/day-away/settings') ? 'bg-cyan-50 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-300' :
              'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <SettingsIcon className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/day-away/admin')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('/day-away/admin') ? 'bg-cyan-50 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-300' :
                'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        <UserInfoSection
          displayName={displayName}
          isCollapsed={isCollapsed && !isMobileMenuOpen}
        />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative bg-gray-50 dark:bg-gray-900">
        <Outlet />
      </main>

      {/* Feedback Modal */}
      <FeedbackModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        appName="Day Away Board"
      />
    </div>
  );
};

export default DayAwayLayout;
